/**
 * Creates SVG polygon points for a thin strip along a line segment.
 * The strip has the given half-width perpendicular to the line direction.
 */
export function lineHitPolygon(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  halfWidth: number,
): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) {
    // Degenerate line — return a small square
    return `${x1 - halfWidth},${y1 - halfWidth} ${x1 + halfWidth},${y1 - halfWidth} ${x1 + halfWidth},${y1 + halfWidth} ${x1 - halfWidth},${y1 + halfWidth}`;
  }
  // Perpendicular unit vector scaled by halfWidth
  const px = (-dy / len) * halfWidth;
  const py = (dx / len) * halfWidth;
  return `${x1 + px},${y1 + py} ${x2 + px},${y2 + py} ${x2 - px},${y2 - py} ${x1 - px},${y1 - py}`;
}

/**
 * Returns props for 4 rects forming a hollow frame (top/right/bottom/left)
 * around a shape. Each rect is a thin strip along one edge with `padding`
 * extending both inward and outward. Uses explicit rects instead of evenodd
 * paths to avoid browser hit-testing bugs under preserveAspectRatio="none".
 */
export function perimeterHitRects(
  x: number,
  y: number,
  width: number,
  height: number,
  padding: number,
): Array<{ x: number; y: number; width: number; height: number }> {
  return [
    // Top edge
    { x: x - padding, y: y - padding, width: width + padding * 2, height: padding * 2 },
    // Bottom edge
    { x: x - padding, y: y + height - padding, width: width + padding * 2, height: padding * 2 },
    // Left edge (between top and bottom to avoid overlap)
    { x: x - padding, y: y + padding, width: padding * 2, height: Math.max(0, height - padding * 2) },
    // Right edge
    { x: x + width - padding, y: y + padding, width: padding * 2, height: Math.max(0, height - padding * 2) },
  ];
}
